/**
 * Structured inputs for the four firewall rule fields that take ranges and lists: source and
 * destination address, source and destination port.
 *
 * Admin counterpart of the client console's `FirewallValueInput`; kept in step by hand, as the
 * other firewall helpers are. The operator first picks what kind of value they mean - all, one, a
 * subnet, a range or a list - and gets exactly the inputs that kind needs. The parse / serialize /
 * check helpers live in `lib/firewallRules.ts`; this file only renders them.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  addressProblem,
  isListAddress,
  isPortItem,
  parseAddress,
  parsePort,
  portListEntry,
  portProblem,
  serializeAddress,
  serializePort,
  withoutSpaces,
  type AddressMode,
  type PortMode,
} from "@/lib/firewallRules";

/**
 * Keeps a structured draft in step with the form's string. The draft is the source while the
 * operator types (an unfinished range has no string of its own); a string the form changes from
 * outside - a macro clearing the ports, a reset - is parsed back in. `problem` is suppressed while
 * the value is still what the dialog opened with, so an old rule is not blocked by a field nobody
 * touched - the same rule `validateFirewallRule` applies with a baseline.
 */
function useDraft<TDraft extends { mode: string }>(
  value: string,
  onChange: (value: string) => void,
  parse: (value: string) => TDraft,
  serialize: (draft: TDraft) => string,
  check: (draft: TDraft) => string | undefined,
  onProblemChange?: (blocked: boolean) => void,
  inactive = false,
) {
  const [draft, setDraft] = useState<TDraft>(() => parse(value));
  const lastEmitted = useRef(value);
  const initial = useRef({ value, mode: parse(value).mode });

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setDraft(parse(value));
  }, [value, parse]);

  const update = (next: TDraft) => {
    setDraft(next);
    const text = serialize(next);
    lastEmitted.current = text;
    onChange(text);
  };

  const untouched =
    draft.mode === initial.current.mode && serialize(draft) === initial.current.value;
  const problem = untouched || inactive ? undefined : check(draft);

  // Through a ref: the form passes a fresh callback every render, and re-running these effects on
  // each one would report "cleared" then "blocked" in a loop.
  const reportRef = useRef(onProblemChange);
  reportRef.current = onProblemChange;
  useEffect(() => {
    reportRef.current?.(!!problem);
  }, [problem]);
  useEffect(() => () => reportRef.current?.(false), []);

  return { draft, update, problem };
}

/** The kind of value, as a compact select in front of the inputs it opens. */
function KindSelect<TMode extends string>({
  labelId,
  value,
  options,
  wide,
  onChange,
}: {
  labelId: string;
  value: TMode;
  options: { value: TMode; label: string }[];
  /** "All" has no inputs beside it, so the select takes the whole row instead of leaving a gap. */
  wide: boolean;
  onChange: (mode: TMode) => void;
}) {
  return (
    <Select value={value} onValueChange={(mode: string) => onChange(mode as TMode)}>
      <SelectTrigger
        className={cn("shrink-0 cursor-pointer", wide ? "w-full" : "w-36")}
        aria-labelledby={labelId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} className="cursor-pointer">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Chips for the entries already added, then a box for the next one. */
function ChipList({
  id,
  items,
  pending,
  isValid,
  invalid,
  placeholder,
  removeLabel,
  describedBy,
  normalize,
  onChange,
  onBlur,
}: {
  id: string;
  items: string[];
  pending: string;
  isValid: (item: string) => boolean;
  invalid: boolean;
  placeholder: string;
  removeLabel: string;
  describedBy?: string;
  normalize: (value: string) => string;
  onChange: (items: string[], pending: string) => void;
  onBlur: () => void;
}) {
  // Every finished entry becomes a chip - valid or not - so a pasted list stays whole and a bad
  // entry shows up red where the operator can see and remove it.
  const commit = (text: string) => {
    const parts = text.split(",");
    const last = parts.pop() ?? "";
    // A value already in the list adds nothing - Proxmox would store it twice.
    const next = [...items];
    for (const part of parts.map(normalize)) {
      if (part && !next.includes(part)) next.push(part);
    }
    onChange(next, last);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commit(`${pending},`);
      return;
    }
    if (event.key === "Backspace" && !pending && items.length > 0) {
      onChange(items.slice(0, -1), "");
    }
  };

  return (
    <div
      className={cn(
        "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input px-2 py-1",
        invalid && "border-destructive",
      )}
    >
      {items.map((item, index) => (
        <span
          key={`${item}-${index}`}
          className={cn(
            "inline-flex items-center gap-1 rounded bg-panel-2 px-2 py-0.5 font-mono text-xs",
            !isValid(item) && "bg-destructive/10 text-destructive",
          )}
        >
          {item}
          <button
            type="button"
            aria-label={`${removeLabel} ${item}`}
            className="cursor-pointer rounded hover:text-destructive"
            onClick={() => onChange(items.filter((_, i) => i !== index), pending)}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        id={id}
        value={pending}
        placeholder={items.length === 0 ? placeholder : undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        className="min-w-[8rem] flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-placeholder"
        onChange={(event) => commit(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          commit(`${pending},`);
          onBlur();
        }}
      />
    </div>
  );
}

/** Two boxes joined by "to". */
function RangeBoxes({
  id,
  from,
  to,
  fromPlaceholder,
  toPlaceholder,
  invalid,
  describedBy,
  fromLabel,
  toLabel,
  joinText,
  inputMode,
  onChange,
  onBlur,
}: {
  id: string;
  from: string;
  to: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  invalid: boolean;
  describedBy?: string;
  fromLabel: string;
  toLabel: string;
  joinText: string;
  inputMode?: "numeric";
  onChange: (from: string, to: string) => void;
  onBlur: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        value={from}
        inputMode={inputMode}
        placeholder={fromPlaceholder}
        aria-label={fromLabel}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value, to)}
        onBlur={onBlur}
      />
      <span className="shrink-0 text-sm text-muted-foreground">{joinText}</span>
      <Input
        value={to}
        inputMode={inputMode}
        placeholder={toPlaceholder}
        aria-label={toLabel}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event) => onChange(from, event.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}

// "Nothing typed yet" is not worth a red line the moment a kind is picked; it shows once the
// operator leaves the box. Save is held back either way.
const EMPTY_PROBLEMS = new Set([
  "Enter an IP address.",
  "Enter a port.",
  "Fill in both ends of the range.",
  "Add at least one entry.",
]);

/**
 * The message to show under a field: the draft's own problem, else the form's check on the string.
 * Nothing for "all", and nothing for an empty box the customer has not left yet.
 */
function visibleMessage(
  mode: string,
  problem: string | undefined,
  formError: string | undefined,
  touched: boolean,
): string | undefined {
  if (mode === "all") return undefined;
  if (problem && EMPTY_PROBLEMS.has(problem) && !touched) return undefined;
  return problem ?? formError;
}

function FieldError({
  id,
  message,
  className,
}: {
  id: string;
  message?: string;
  className?: string;
}) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className={cn("text-sm text-destructive", className)}>
      {message}
    </p>
  );
}

const CUSTOM_HINT =
  "This value is not in one of the forms above. Edit it here, or pick a form to replace it.";

function FieldLabel({
  id,
  htmlFor,
  label,
}: {
  id?: string;
  htmlFor?: string;
  label: string;
}) {
  // Top-aligned with the controls rather than centred, so it stays beside the first line when a
  // list of chips wraps onto several.
  return (
    <Label id={id} htmlFor={htmlFor} className="pt-2.5">
      {label}
    </Label>
  );
}

/** Label in a narrow left column, the kind and its inputs beside it, messages under the inputs. */
const FIELD_ROW = "grid grid-cols-[3.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1.5";

const onlyDigits = (value: string): string => value.replace(/\D/g, "");

// ------------------------------------------------------------------------------------------------
// The two inputs

interface ValueInputProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  /** The form's own message for the field (`validateFirewallRule`), shown when the draft has none. */
  error?: string;
  /** Reports whether the field must hold the Save button back. */
  onProblemChange?: (blocked: boolean) => void;
}

/** Source or destination address: all, one IP, a subnet, an IP range, or a list of IPs/subnets. */
export function AddressInput({
  label,
  value,
  onChange,
  error,
  onProblemChange,
}: ValueInputProps) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const { draft, update, problem } = useDraft(
    value ?? "",
    onChange,
    parseAddress,
    serializeAddress,
    addressProblem,
    onProblemChange,
  );
  const message = visibleMessage(draft.mode, problem, error, touched);
  const invalid = !!message;
  const describedBy = message ? `${id}-error` : undefined;
  const markTouched = () => setTouched(true);

  const options: { value: AddressMode; label: string }[] = [
    { value: "all", label: "All" },
    { value: "single", label: "Single IP" },
    { value: "subnet", label: "Subnet" },
    // "IP range" is hidden for now - not in use yet. To offer it again, restore the line below and
    // drop the `draft.mode === "range"` push underneath.
    // { value: "range", label: "IP range" },
    { value: "list", label: "List" },
  ];
  // A rule already stored as a range still opens as one, so an edit never loses it.
  if (draft.mode === "range") options.push({ value: "range", label: "IP range" });
  if (draft.mode === "custom") options.push({ value: "custom", label: "Custom" });

  return (
    <div className={FIELD_ROW}>
      <FieldLabel
        id={`${id}-label`}
        htmlFor={draft.mode === "all" ? undefined : `${id}-input`}
        label={label}
      />
      <div className="flex min-w-0 items-start gap-2">
        <KindSelect
          labelId={`${id}-label`}
          value={draft.mode}
          options={options}
          wide={draft.mode === "all"}
          onChange={(mode) => {
            setTouched(false);
            update({ ...draft, mode });
          }}
        />
        <div className={cn("min-w-0 flex-1", draft.mode === "all" && "hidden")}>
          {draft.mode === "single" && (
            <Input
              id={`${id}-input`}
              value={draft.single}
              placeholder="10.0.0.5"
              aria-invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => update({ ...draft, single: withoutSpaces(event.target.value) })}
              onBlur={markTouched}
            />
          )}
          {draft.mode === "subnet" && (
            <div className="flex items-center gap-2">
              <Input
                id={`${id}-input`}
                value={draft.subnetIp}
                placeholder="10.0.0.0"
                aria-invalid={invalid}
                aria-describedby={describedBy}
                onChange={(event) => update({ ...draft, subnetIp: withoutSpaces(event.target.value) })}
                onBlur={markTouched}
              />
              <span className="text-sm text-muted-foreground">/</span>
              <Select
                value={draft.subnetPrefix}
                onValueChange={(prefix: string) => update({ ...draft, subnetPrefix: prefix })}
              >
                <SelectTrigger className="w-20 shrink-0 cursor-pointer" aria-label="Prefix length">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 33 }, (_, i) => String(32 - i)).map((prefix) => (
                    <SelectItem key={prefix} value={prefix} className="cursor-pointer">
                      {prefix}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {draft.mode === "range" && (
            <RangeBoxes
              id={`${id}-input`}
              from={draft.rangeFrom}
              to={draft.rangeTo}
              fromPlaceholder="10.0.0.1"
              toPlaceholder="10.0.0.50"
              fromLabel={`${label} - from`}
              toLabel={`${label} - to`}
              joinText="to"
              invalid={invalid}
              describedBy={describedBy}
              onChange={(from, to) =>
                update({ ...draft, rangeFrom: withoutSpaces(from), rangeTo: withoutSpaces(to) })
              }
              onBlur={markTouched}
            />
          )}
          {draft.mode === "list" && (
            <ChipList
              id={`${id}-input`}
              items={draft.items}
              pending={draft.pending}
              isValid={isListAddress}
              invalid={invalid}
              placeholder="IP or subnet, then Enter — e.g. 10.0.0.5, 10.0.0.0/24"
              removeLabel="Remove"
              describedBy={describedBy}
              normalize={withoutSpaces}
              onChange={(items, pending) => update({ ...draft, items, pending })}
              onBlur={markTouched}
            />
          )}
          {draft.mode === "custom" && (
            <Input
              id={`${id}-input`}
              value={draft.custom}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => update({ ...draft, custom: withoutSpaces(event.target.value) })}
            />
          )}
        </div>
      </div>
      <FieldError id={`${id}-error`} message={message} className="col-start-2" />
      {draft.mode === "custom" && <p className="col-start-2 text-sm text-muted-foreground">{CUSTOM_HINT}</p>}
    </div>
  );
}

/** Source or destination port: all, one port, a port range, or a list of ports and ranges. */
export function PortInput({
  label,
  value,
  onChange,
  error,
  onProblemChange,
  disabled,
}: ValueInputProps & {
  /** A macro sets the ports: the field shows that instead of its inputs. */
  disabled?: boolean;
}) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const { draft, update, problem } = useDraft(
    value ?? "",
    onChange,
    parsePort,
    serializePort,
    portProblem,
    onProblemChange,
    disabled,
  );
  const message = disabled ? undefined : visibleMessage(draft.mode, problem, error, touched);
  const invalid = !!message;
  const describedBy = message ? `${id}-error` : undefined;
  const markTouched = () => setTouched(true);

  if (disabled) {
    return (
      <div className={FIELD_ROW}>
        <FieldLabel htmlFor={`${id}-input`} label={label} />
        <Input id={`${id}-input`} disabled placeholder="Set by macro" />
      </div>
    );
  }

  const options: { value: PortMode; label: string }[] = [
    { value: "all", label: "All" },
    { value: "single", label: "Single port" },
    { value: "range", label: "Port range" },
    { value: "list", label: "List" },
  ];
  if (draft.mode === "custom") options.push({ value: "custom", label: "Custom" });

  return (
    <div className={FIELD_ROW}>
      <FieldLabel
        id={`${id}-label`}
        htmlFor={draft.mode === "all" ? undefined : `${id}-input`}
        label={label}
      />
      <div className="flex min-w-0 items-start gap-2">
        <KindSelect
          labelId={`${id}-label`}
          value={draft.mode}
          options={options}
          wide={draft.mode === "all"}
          onChange={(mode) => {
            setTouched(false);
            update({ ...draft, mode });
          }}
        />
        <div className={cn("min-w-0 flex-1", draft.mode === "all" && "hidden")}>
          {draft.mode === "single" && (
            <Input
              id={`${id}-input`}
              value={draft.single}
              inputMode="numeric"
              placeholder="443"
              aria-invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => update({ ...draft, single: onlyDigits(event.target.value) })}
              onBlur={markTouched}
            />
          )}
          {draft.mode === "range" && (
            <RangeBoxes
              id={`${id}-input`}
              from={draft.rangeFrom}
              to={draft.rangeTo}
              fromPlaceholder="8000"
              toPlaceholder="8080"
              fromLabel={`${label} - from`}
              toLabel={`${label} - to`}
              joinText="to"
              inputMode="numeric"
              invalid={invalid}
              describedBy={describedBy}
              onChange={(from, to) =>
                update({ ...draft, rangeFrom: onlyDigits(from), rangeTo: onlyDigits(to) })
              }
              onBlur={markTouched}
            />
          )}
          {draft.mode === "list" && (
            <ChipList
              id={`${id}-input`}
              items={draft.items}
              pending={draft.pending}
              isValid={isPortItem}
              invalid={invalid}
              placeholder="Port or range, then Enter — e.g. 80, 8000:8080"
              removeLabel="Remove"
              describedBy={describedBy}
              normalize={portListEntry}
              onChange={(items, pending) => update({ ...draft, items, pending })}
              onBlur={markTouched}
            />
          )}
          {draft.mode === "custom" && (
            <Input
              id={`${id}-input`}
              value={draft.custom}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => update({ ...draft, custom: withoutSpaces(event.target.value) })}
            />
          )}
        </div>
      </div>
      <FieldError id={`${id}-error`} message={message} className="col-start-2" />
      {draft.mode === "custom" && <p className="col-start-2 text-sm text-muted-foreground">{CUSTOM_HINT}</p>}
    </div>
  );
}

/**
 * One side of the connection - source or destination - holding its address and its port, so the
 * two read as a pair ("from 10.0.0.0/24 port any, to this VM port 443") rather than four loose rows.
 */
export function RuleSide({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={`${id}-title`}
      className="space-y-2.5 rounded-lg border border-hairline p-3"
    >
      <p id={`${id}-title`} className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {title}
        <span className="ml-1.5 font-normal normal-case tracking-normal">· {hint}</span>
      </p>
      {children}
    </div>
  );
}
